/** Wiki review queue ("Chờ vào Wiki"): proposals agents made, filtered by status; the second tab of the project Sources screen */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle } from 'lucide-react'
import { useWikiProposals, type ProposalStatusFilter } from '../hooks/use-project-content'
import { useAppStore } from '../stores/app-store'
import { projectPath } from '../components/shell/nav-config'
import { useT } from '../i18n'
import type { TranslationKey } from '../i18n'

const FILTERS: Array<{ value: ProposalStatusFilter; key: TranslationKey }> = [
  { value: 'pending', key: 'review.status.pending' },
  { value: 'applied', key: 'review.status.applied' },
  { value: 'discarded', key: 'review.status.discarded' },
  { value: 'all', key: 'review.status.all' },
]

export function ProjectReviewPanel({ projectSlug }: { projectSlug: string }) {
  const t = useT()
  const locale = useAppStore((s) => s.locale)
  const [status, setStatus] = useState<ProposalStatusFilter>('pending')
  const { data, isLoading, error } = useWikiProposals(projectSlug, status)
  const dateTime = new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' })

  return (
    <div className="space-y-6">
      <div className="flex w-fit rounded-lg border border-border p-0.5" role="group">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            aria-pressed={status === f.value}
            onClick={() => setStatus(f.value)}
            className={`cursor-pointer rounded-md px-3 py-1.5 text-sm ${status === f.value ? 'bg-surface-hover text-fg' : 'text-fg-muted hover:text-fg'}`}
          >
            {t(f.key)}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="text-sm text-fg-subtle">{t('common.loading')}</p>
      ) : error ? (
        <p role="alert" className="text-sm text-danger">{t('review.loadError')}</p>
      ) : !data?.proposals.length ? (
        <p className="rounded-xl border border-dashed border-border p-6 text-sm text-fg-muted">{t('review.empty')}</p>
      ) : (
        <ul className="divide-y divide-border rounded-xl border border-border">
          {data.proposals.map((p) => (
            <li key={p.id}>
              <Link to={projectPath(projectSlug, `review/${p.id}`)} className="flex flex-wrap items-start justify-between gap-2 px-4 py-3 hover:bg-surface-hover">
                <span className="min-w-0 space-y-0.5">
                  <span className="block font-medium text-fg">{p.title}</span>
                  <span className="block text-xs text-fg-subtle">
                    {p.slug} · {p.baseVersion ? t('review.basedOn', { version: p.baseVersion }) : t('review.newPage')}
                    {' · '}{t(p.actorType === 'api_key' ? 'review.by.agent' : 'review.by.user')}
                    {' · '}{dateTime.format(new Date(p.createdAt))}
                  </span>
                  {p.changeSummary && <span className="block text-sm text-fg-muted">{p.changeSummary}</span>}
                </span>
                <span className="flex shrink-0 items-center gap-2 text-xs">
                  {p.status === 'pending' && (p.checks.blocking.length > 0 || p.checks.contradictions.length > 0) && (
                    <AlertTriangle className="h-4 w-4 text-danger" aria-label={t('review.checks')} />
                  )}
                  <span className="rounded-md border border-border px-2 py-0.5 text-fg-muted">{t(`review.status.${p.status}` as TranslationKey)}</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
